import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Tippy from '@tippyjs/react/headless';
import Toast from '@/components/common/Toast';
import { useAuth } from '@/hooks/useAuth';
import { useMembershipStore } from '@/stores/membershipStore';

export interface UserPointsBadgeProps {
  className?: string;
  /** 弹出位置方向，不传则由 Tippy 自动控制 */
  placement?:
  | 'auto'
  | 'auto-start'
  | 'auto-end'
  | 'top'
  | 'top-start'
  | 'top-end'
  | 'bottom'
  | 'bottom-start'
  | 'bottom-end'
  | 'right'
  | 'right-start'
  | 'right-end'
  | 'left'
  | 'left-start'
  | 'left-end';
}

export function UserPointsBadge({ className = '', placement }: UserPointsBadgeProps) {
  const { rawUser } = useAuth();
  const {
    openMembershipModal,
    checkInStatus,
    isCheckInLoading,
    fetchCheckInStatus: loadCheckInStatus,
    doCheckIn,
  } = useMembershipStore();

  const [open, setOpen] = useState(false);
  const [toast, setToast] = useState<{ message: string; type?: 'success' | 'error' | 'info' | 'warning' } | null>(null);

  // 判断是否拥有终身免费/不限积分权益 (POINT_CONTENT_UNLIMITED)
  const isUnlimitedPoints = Boolean(
    rawUser?.membership?.subscriptions?.[0]?.benefits_snapshot?.POINT_CONTENT_UNLIMITED
  );

  // 从账号信息 rawUser.membership.wallet 中读取积分钱包信息
  const wallet = rawUser?.membership?.wallet;
  const membershipPoints = wallet?.membership_balance ?? 0;
  const purchasedPoints = wallet?.purchased_balance ?? 0;
  const totalPoints = wallet?.total_balance ?? 0;

  const checkedIn = checkInStatus ? Boolean(checkInStatus.checked_in) : null;

  // 查询每日签到状态
  useEffect(() => {
    if (!isUnlimitedPoints) {
      void loadCheckInStatus();
    }
  }, [isUnlimitedPoints, loadCheckInStatus]);

  const showToast = (message: string, type: 'success' | 'error' | 'info' | 'warning' = 'info') => {
    setToast({ message, type });
  };

  // 极速每日签到逻辑
  const handleCheckIn = async (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (isCheckInLoading) return;

    try {
      const res = await doCheckIn();
      if (res) {
        const amount = res.award_amount ?? 10;
        showToast(`签到成功！+${amount} 积分`, 'success');
      }
    } catch (err: any) {
      showToast(err?.message || '签到失败，请重试', 'error');
    }
  };

  if (isUnlimitedPoints) {
    return null;
  }

  return (
    <div className={`relative hidden sm:block shrink-0 ${className}`}>
      {/* 签到 Toast 提示 */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <Tippy
        interactive={true}
        visible={open}
        onClickOutside={() => setOpen(false)}
        placement={placement}
        offset={[0, 8]}
        appendTo={() => document.body}
        render={(attrs) => (
          <div
            {...attrs}
            className="w-56 rounded-2xl border border-amber-200/70 bg-white/95 p-3.5 shadow-xl backdrop-blur-xl z-50 outline-none animate-in fade-in zoom-in-95 duration-150"
          >
            {/* 标题头部 */}
            <div className="mb-2 flex items-center justify-between text-amber-700">
              <div className="flex items-center gap-2">
                <i className="ri-wallet-3-line text-base"></i>
                <span className="text-xs font-semibold">积分钱包</span>
              </div>
              <Link
                to="/point-transactions"
                onClick={() => setOpen(false)}
                className="text-[11px] font-medium text-amber-600 hover:text-amber-800 hover:underline flex items-center gap-0.5 cursor-pointer"
              >
                <span>流水明细</span>
                <i className="ri-arrow-right-s-line"></i>
              </Link>
            </div>

            {/* 积分明细列表 */}
            <div className="space-y-1.5 text-xs text-slate-600">
              <div className="flex items-center justify-between gap-3">
                <span>会员赠送</span>
                <span className="font-semibold text-slate-800">{membershipPoints}</span>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span>充值购买</span>
                <span className="font-semibold text-slate-800">{purchasedPoints}</span>
              </div>
              <div className="h-px bg-slate-100 my-1"></div>
              <div className="flex items-center justify-between gap-3">
                <span className="font-medium text-slate-700">总积分</span>
                <span className="font-bold text-amber-600">{totalPoints}</span>
              </div>
            </div>

            {/* 底部功能按钮 */}
            <div className="mt-3 pt-2 border-t border-slate-100">
              {checkedIn === false ? (
                <button
                  type="button"
                  onClick={handleCheckIn}
                  disabled={isCheckInLoading}
                  className="w-full text-center py-1.5 px-3 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold text-xs rounded-xl transition-all flex items-center justify-center gap-1.5 cursor-pointer shadow-xs"
                >
                  <i className="ri-calendar-check-line text-xs"></i>
                  {isCheckInLoading ? '签到中...' : '一键每日签到'}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    openMembershipModal();
                  }}
                  className="w-full text-center py-1.5 px-3 bg-amber-50 hover:bg-amber-100 border border-amber-200/60 rounded-xl text-amber-800 font-semibold text-xs transition-all flex items-center justify-center gap-1.5 cursor-pointer"
                >
                  <i className="ri-vip-crown-line text-xs"></i>
                  充值积分 / 开通会员
                </button>
              )}
            </div>
          </div>
        )}
      >
        {/* 会员积分 / 签到 Trigger 触发按钮 */}
        <button
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          title={checkedIn === false ? '点击查看积分与签到' : '查看我的积分与权益'}
          aria-expanded={open}
          disabled={isCheckInLoading}
          className={`flex items-center gap-1.5 px-2.5 py-1 lg:px-3 lg:py-1.5 rounded-full border text-xs lg:text-sm font-semibold transition-all cursor-pointer shadow-2xs ${checkedIn === false
              ? 'bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-500 hover:to-orange-600 border-amber-300 text-white animate-pulse'
              : 'bg-gradient-to-r from-amber-50 to-orange-50/80 hover:from-amber-100 hover:to-orange-100 border-amber-200/80 text-amber-800'
            }`}
        >
          <i
            className={`${checkedIn === false ? 'ri-calendar-check-line' : 'ri-coin-line'
              } ${checkedIn === false ? 'text-white' : 'text-amber-500'} text-sm lg:text-base`}
          ></i>
          <span>
            {checkedIn === false ? (isCheckInLoading ? '签到中...' : '签到') : `${totalPoints} 积分`}
          </span>
        </button>
      </Tippy>
    </div>
  );
}

export default UserPointsBadge;

